import { getLocalDateString } from '@@utils/getLocalDateString';

import {
    CERTIFICATION_STATUS_EXPIRED,
    CERTIFICATION_STATUS_EXPIRING,
    getCertificationStatus,
    getDaysUntilExpiration
} from '@@utils/certifications';

import type { ApplicatorCertificationResponse, CertificationTooltipTranslations } from './types';

export const TEST_ID = 'certificationStatesCell';

export const ADD_CERTIFICATION_TEST_ID = 'certificationStatesCellAddCertification';

export const HIDDEN_COUNT_PREFIX = '+';

export const NO_HIDDEN_CERTIFICATIONS = 0;

// The column shows three chips at most, so an applicator certified across many
// states still leaves the rest of the row readable.
export const MAX_VISIBLE_CERTIFICATIONS = 3;

export const HIDDEN_STATE_SEPARATOR = ', ';

export const TOOLTIP_DATE_LOCALE = 'en-US';

export const TOOLTIP_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric'
};

const SINGLE_DAY_COUNT = 1;

const SAME_DAY_COUNT = 0;

export const getVisibleCertifications = (
    certifications?: ApplicatorCertificationResponse[] | null
): ApplicatorCertificationResponse[] => (certifications ?? []).slice(0, MAX_VISIBLE_CERTIFICATIONS);

export const getHiddenCertifications = (
    certifications?: ApplicatorCertificationResponse[] | null
): ApplicatorCertificationResponse[] => (certifications ?? []).slice(MAX_VISIBLE_CERTIFICATIONS);

export const getHiddenCertificationStates = (
    certifications?: ApplicatorCertificationResponse[] | null
): string =>
    getHiddenCertifications(certifications)
        .map(certification => certification.stateOfCertification)
        .join(HIDDEN_STATE_SEPARATOR);

// The hover text names the record and says how its expiry reads today, so an admin
// can tell two chips of the same state apart without opening the member.
export const getCertificationTooltip = (
    certification: ApplicatorCertificationResponse,
    translations: CertificationTooltipTranslations
): string => {
    const { applicatorCertificationNumber, certificationExpirationDate } = certification;

    if (!certificationExpirationDate) {
        return translations.noExpiration(applicatorCertificationNumber);
    }

    const expirationDate = getLocalDateString(
        certificationExpirationDate,
        TOOLTIP_DATE_LOCALE,
        TOOLTIP_DATE_FORMAT_OPTIONS
    );

    const status = getCertificationStatus(certificationExpirationDate);

    if (status === CERTIFICATION_STATUS_EXPIRED) {
        return translations.expired(applicatorCertificationNumber, expirationDate);
    }

    if (status !== CERTIFICATION_STATUS_EXPIRING) {
        return translations.validThrough(applicatorCertificationNumber, expirationDate);
    }

    const dayCount = getDaysUntilExpiration(certificationExpirationDate) ?? SAME_DAY_COUNT;

    if (dayCount === SAME_DAY_COUNT) {
        return translations.expiresToday(applicatorCertificationNumber);
    }

    return dayCount === SINGLE_DAY_COUNT
        ? translations.expiresInDay(applicatorCertificationNumber)
        : translations.expiresInDays(applicatorCertificationNumber, dayCount);
};
